import { BUCKET_MAPPINGS } from './bucket-mappings';

/**
 * Checks if a URL is a data URL
 */
function isDataURL(url: string): boolean {
  return url.startsWith('data:');
}

/**
 * Checks if a URL contains a recognized S3 bucket name
 */
function containsRecognizedBucket(url: string): boolean {
  for (const bucketName of Object.keys(BUCKET_MAPPINGS)) {
    if (url.includes(`/${bucketName}/`)) {
      return true;
    }
  }

  return false;
}

/**
 * Maps S3 bucket URLs to CDN URLs based on BUCKET_MAPPINGS.
 *
 * This is an internal function that performs pure bucket-to-CDN mapping.
 * It replaces the bucket name in the URL with the corresponding CDN folder URL.
 *
 * For buckets with pattern-based mappings, it first checks if any pattern matches
 * the object key (path). If no pattern matches, it falls back to the default CDN URL
 * for that bucket (if available).
 *
 * @param url - The S3 URL to transform
 * @returns The CDN URL if mapping found, null otherwise
 * @internal Use getCdnUrl() for public API with fallback support
 */
export function mapBucketToCdn(url: string): string | null {
  try {
    // Iterate through all bucket mappings
    for (const bucketName of Object.keys(BUCKET_MAPPINGS)) {
      const bucketPattern = `/${bucketName}/`;

      if (!url.includes(bucketPattern)) continue;

      const remainingUrl = url.split(bucketPattern)[1];
      const mapping = BUCKET_MAPPINGS[bucketName];

      // Simple string mapping
      if (typeof mapping === 'string') {
        return `https://${mapping}/${remainingUrl}`;
      }

      // Pattern-based mapping
      if (mapping && typeof mapping === 'object' && 'patterns' in mapping) {
        // Check patterns in order (first match wins)
        for (const patternMapping of mapping.patterns) {
          if (patternMapping.pattern.test(remainingUrl)) {
            return `https://${patternMapping.cdnUrl}/${remainingUrl}`;
          }
        }

        // Fall back to default CDN URL if no pattern matches
        if (mapping.default) {
          return `https://${mapping.default}/${remainingUrl}`;
        }
      }
    }

    return null;
  } catch (error) {
    console.error('mapBucketToCdn: Invalid URL provided.', error);
    return null;
  }
}

/**
 * Transforms URLs to their CloudFront (CDN) equivalents.
 *
 * This is the public API for URL transformation with multiple fallback strategies:
 * 1. First tries bucket-to-CDN mapping via mapBucketToCdn()
 * 2. Falls back to Fluer-specific URL transformation
 * 3. Falls back to regex-based domain extraction for legacy URLs
 * 4. Returns original URL if no mapping is found
 *
 * @param url - The original URL to transform
 * @param isQa - Whether to use QA environment URLs (default: false)
 * @returns The CDN URL if transformation succeeds, otherwise the original URL
 */
export function getCdnUrl(url: string, isQa?: boolean): string {
  let newUrl = url;

  if (url && typeof url === 'string' && !isDataURL(url)) {
    const cdnUrl = mapBucketToCdn(url);

    if (cdnUrl) return cdnUrl;

    if (url.match(/.*\/([^/\s]+(fluer)[^/\s]+\/)(.*)/)) {
      return url.replace(/https?:\/\/s3[^/]*\.amazonaws\.com\//, 'https://');
    }

    if (containsRecognizedBucket(url)) {
      return url;
    }

    let path;
    const pathStartsWithSlash = url.match(/^\/.*/);

    if (pathStartsWithSlash) path = pathStartsWithSlash[0];

    const pathMatchesDomain = url.match(
      /.*\/([^/\s]+(inkive|desygner|qadesygner|webrand|photodexia)[^/\s]+\/)(.*)/
    );

    if (pathMatchesDomain) path = pathMatchesDomain[3];

    if (path) {
      if (isQa) {
        newUrl = `https://static.qadesygner.xyz/${path}`;
      } else {
        newUrl = `https://static.desygner.com/${path}`;
      }
    }
  }

  return newUrl;
}
